import { useState } from "react";
import {
  BarChart3,
  Bath,
  Droplets,
  Heart,
  Zap,
} from "lucide-react";
import HygieneWashSchoolsModal from "@/components/HygieneWashSchoolsModal";
import HeroStatusChip from "@/components/HeroStatusChip";
import { toTitleCase } from "../utils/toTitleCase";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");

const buildGaugeStyle = (value, color = "#10b981") => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, rgba(16,185,129,.12) ${Math.min(value, 100)}% 100%)`,
});

const buildMiniRingStyle = (value, color = "#10b981", max = 100) => {
  const pctFill = Math.min((value / max) * 100, 100);
  return {
    background: `radial-gradient(circle at center, #fff 0 58%, transparent 59%), conic-gradient(${color} 0 ${pctFill}%, rgba(16,185,129,.14) ${pctFill}% 100%)`,
  };
};

const RADAR_AXES = [
  { key: "handwash", label: "Handwash", angle: -90 },
  { key: "sanitary", label: "Sanitary Pad", angle: -18 },
  { key: "electricity", label: "Electricity", angle: 54 },
  { key: "water", label: "Toilet Water", angle: 126 },
  { key: "functional", label: "Functional", angle: -162 },
];

const radarPoint = (angleDeg, value, maxRadius = 145) => {
  const r = (Math.min(value, 100) / 100) * maxRadius;
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: Math.round(r * Math.cos(rad) * 10) / 10,
    y: Math.round(r * Math.sin(rad) * 10) / 10,
  };
};

const WashRadarChart = ({ metrics }) => {
  const dataPoints = RADAR_AXES.map((axis) => radarPoint(axis.angle, metrics[axis.key] ?? 0));
  const polygon = dataPoints.map((p) => `${p.x},${p.y}`).join(" ");

  const gridLevels = [145, 110, 75, 40];
  const gridPolygons = gridLevels.map((level) =>
    RADAR_AXES.map((axis) => {
      const p = radarPoint(axis.angle, 100, level);
      return `${p.x},${p.y}`;
    }).join(" ")
  );

  return (
    <div className="ct-radar-box">
      <svg viewBox="0 0 420 380" role="img" aria-label="WASH compliance radar chart">
        <g transform="translate(210 190)">
          {gridPolygons.map((points, idx) => (
            <polygon key={idx} points={points} fill="none" stroke="#d7e3f6" strokeWidth="1.5" />
          ))}
          {RADAR_AXES.map((axis) => {
            const end = radarPoint(axis.angle, 100);
            return <line key={axis.key} x1="0" y1="0" x2={end.x} y2={end.y} stroke="#d7e3f6" />;
          })}
          <polygon points={polygon} fill="rgba(37,99,235,.25)" stroke="#2563eb" strokeWidth="3" />
          {dataPoints.map((p, idx) => (
            <circle key={idx} cx={p.x} cy={p.y} r="4" fill="#2563eb" />
          ))}
          <text x="0" y="-168" textAnchor="middle" className="ct-radar-label">
            Handwash
          </text>
          <text x="158" y="-42" textAnchor="start" className="ct-radar-label">
            Sanitary Pad
          </text>
          <text x="102" y="145" textAnchor="start" className="ct-radar-label">
            Electricity
          </text>
          <text x="-103" y="145" textAnchor="end" className="ct-radar-label">
            Toilet Water
          </text>
          <text x="-158" y="-42" textAnchor="end" className="ct-radar-label">
            Functional
          </text>
          {dataPoints.map((p, idx) => (
            <text
              key={`num-${idx}`}
              x={p.x + (p.x >= 0 ? 12 : -12)}
              y={p.y + (p.y >= 0 ? 14 : -8)}
              className="ct-radar-num"
              textAnchor={p.x >= 0 ? "start" : "end"}
            >
              {Math.round(metrics[RADAR_AXES[idx].key] ?? 0)}
            </text>
          ))}
        </g>
      </svg>
    </div>
  );
};

const FacilityBar = ({ labelLines, value, tone = "" }) => (
  <div className="ct-hbar-row">
    <div className="ct-hbar-label">
      {labelLines.map((line, index) => (
        <span key={line}>
          {index > 0 && <br />}
          {line}
        </span>
      ))}
    </div>
    <div className="ct-hbar-track">
      <div className={`ct-hbar ${tone}`} style={{ width: `${Math.min(value, 100)}%` }} />
    </div>
    <div className="ct-hbar-value">{pct(value)}</div>
  </div>
);

const getBlockStatus = (washIndex, sufficiency) => {
  if (washIndex >= 98.5 && sufficiency >= 0.85) return { label: "Excellent", tone: "green" };
  if (washIndex >= 98 && sufficiency >= 0.78) return { label: "Good", tone: "blue" };
  return { label: "Watch", tone: "orange" };
};

const HYGIENE_WASH_DRILL_CATEGORIES = {
  no_handwash: { id: "no_handwash", label: "Handwash Coverage" },
  water_gap: { id: "water_gap", label: "Water Coverage" },
  no_sanitary_pad: { id: "no_sanitary_pad", label: "Sanitary Pad Access" },
  no_electricity: { id: "no_electricity", label: "Electricity Readiness" },
  point_sufficiency: { id: "point_sufficiency", label: "Point Sufficiency" },
};

const ScoreCard = ({
  icon: Icon,
  iconTone,
  ringValue,
  ringDisplay,
  ringTone = "",
  ringMax = 100,
  ringColor,
  title,
  description,
  clickable = false,
  onClick,
}) => (
  <article
    className={`ct-score-card${clickable ? " clickable" : ""}`}
    role={clickable ? "button" : undefined}
    tabIndex={clickable ? 0 : undefined}
    onClick={clickable ? onClick : undefined}
    onKeyDown={
      clickable
        ? (e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onClick?.();
            }
          }
        : undefined
    }
    title={clickable ? `View schools for ${title}` : undefined}
  >
    <div className="ct-score-head">
      <div className={`ct-icon-box ${iconTone}`}>
        <Icon size={22} />
      </div>
      <div
        className={`ct-mini-ring ${ringTone}`}
        style={buildMiniRingStyle(ringValue, ringColor || (ringTone === "blue" ? "#2563eb" : ringTone === "purple" ? "#8b5cf6" : "#10b981"), ringMax)}
      >
        {ringDisplay}
      </div>
    </div>
    <h3>{toTitleCase(title)}</h3>
    <p>{description}</p>
  </article>
);

const ClassroomsToiletsHygieneWashTab = ({ overview, blockData, hygieneMetrics, scopeLabel }) => {
  const [activeCategory, setActiveCategory] = useState(null);

  const openCategory = (key, value) => {
    const count = Number(value) || 0;
    if (count <= 0) return;
    setActiveCategory({ ...HYGIENE_WASH_DRILL_CATEGORIES[key], value: fmt(value) });
  };
  if (!overview) {
    return (
      <div className="ct-empty-card">
        <Droplets className="mx-auto mb-3 opacity-40" size={40} />
        <p>No hygiene and WASH data available for the selected scope.</p>
      </div>
    );
  }

  const handwashPct = overview.handwash_coverage_pct ?? hygieneMetrics?.handwash_coverage ?? 0;
  const sanitaryPct = overview.sanitary_pad_pct ?? hygieneMetrics?.sanitary_pad_coverage ?? 0;
  const electricityPct = overview.electricity_pct ?? hygieneMetrics?.electricity_coverage ?? 0;
  const waterPct = overview.water_coverage_pct ?? 0;
  const functionalPct = overview.toilet_functional_pct ?? 0;
  const sufficiency = overview.handwash_sufficiency_ratio ?? hygieneMetrics?.handwash_sufficiency ?? 0;
  const handwashPoints = overview.handwash_points ?? hygieneMetrics?.handwash_points ?? 0;
  const waterGap = overview.water_gap ?? 0;
  const handwashDist = hygieneMetrics?.distribution?.find((d) => d.facility === "Handwash Facility");
  const sanitaryDist = hygieneMetrics?.distribution?.find((d) => d.facility === "Sanitary Pad");
  const electricityDist = hygieneMetrics?.distribution?.find((d) => d.facility === "Electricity");
  const schoolsMonitored = handwashDist?.yes ?? overview.total_schools ?? 0;
  const schoolsWithoutHandwash = handwashDist?.no ?? 0;
  const schoolsWithoutSanitary = sanitaryDist?.no ?? 0;
  const schoolsWithoutElectricity = electricityDist?.no ?? 0;
  const pointSufficiencySchools = hygieneMetrics?.point_sufficiency_schools ?? 0;

  const hygieneWashScore =
    Math.round(
      ((handwashPct + sanitaryPct + electricityPct + waterPct + functionalPct) / 5) * 10
    ) / 10;

  const avgHandwashPerSchool =
    overview.total_schools > 0
      ? Math.round((handwashPoints / overview.total_schools) * 100) / 100
      : 0;

  const essentialUtilities =
    sanitaryPct > 0 && electricityPct > 0
      ? Math.round(((sanitaryPct + electricityPct) / 2) * 10) / 10
      : 0;

  const strongCoverage = handwashPct >= 95 && sanitaryPct >= 95;

  const radarMetrics = {
    handwash: handwashPct,
    sanitary: sanitaryPct,
    electricity: electricityPct,
    water: waterPct,
    functional: functionalPct,
  };

  const rankedBlocks = [...(blockData ?? [])]
    .sort((a, b) => (b.wash_index ?? 0) - (a.wash_index ?? 0))
    .map((block, index) => ({
      ...block,
      rank: index + 1,
      electricity_pct: block.electricity_pct ?? electricityPct,
      sufficiency_ratio:
        block.sufficiency_ratio ??
        (block.total_toilets > 0 && block.handwash_points
          ? Math.round((block.handwash_points / block.total_toilets) * 100) / 100
          : sufficiency),
    }));

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="ct-hw-tab" data-testid="tab-hygiene-content" id="ct-hw-overview">
      <section className="ct-hero-card ct-hero-card-hygiene">
        <div className="ct-hero-layout">
          <div>
            <div className="ct-health-main">
              <div
                className="ct-gauge ct-gauge-success"
                style={buildGaugeStyle(hygieneWashScore)}
                aria-label={`WASH readiness score ${pct(hygieneWashScore)}`}
              >
                <div>
                  <strong>{pct(hygieneWashScore)}</strong>
                  <span>WASH</span>
                </div>
              </div>
              <div className="ct-hero-copy">
                <h2>Hygiene & WASH Command Centre</h2>
                <p>
                  Core hygiene infrastructure is {strongCoverage ? "nearly universal" : "improving"}. The next
                  improvement area is adequacy: the current handwash-point sufficiency ratio is below the ideal
                  service benchmark.
                </p>
                <div className="ct-status-row">
                  <HeroStatusChip chipClass="ct-status-chip" showChevron={false}>
                    {strongCoverage ? "Strong hygiene coverage" : "Coverage improving"}
                  </HeroStatusChip>
                  <HeroStatusChip chipClass="ct-status-chip" variant="blue" showChevron={false}>
                    {fmt(schoolsMonitored)} schools monitored
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="orange"
                    onClick={
                      pointSufficiencySchools > 0
                        ? () => openCategory("point_sufficiency", pointSufficiencySchools)
                        : undefined
                    }
                    ariaLabel={
                      pointSufficiencySchools > 0
                        ? `Sufficiency watch ${sufficiency.toFixed(2)} — view schools below benchmark`
                        : undefined
                    }
                  >
                    Sufficiency watch: {sufficiency.toFixed(2)}
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ct-summary-note">
              <div className="ct-icon-box cyan">
                <Droplets size={22} />
              </div>
              <div>
                <b>Primary action for review</b>
                <span>
                  Prioritize blocks with high enrolment and lower handwash points per toilet before the next WASH
                  compliance audit.
                </span>
              </div>
            </div>
          </div>

          <div className="ct-metric-grid ct-metric-grid-2" aria-label="Hygiene and WASH KPIs">
            <article className="ct-metric-card">
              <small>Handwash Coverage</small>
              <strong>{pct(handwashPct)}</strong>
              <span className="ct-metric-sub">
                {schoolsWithoutHandwash > 0
                  ? `${fmt(schoolsWithoutHandwash)} schools without handwash`
                  : "All schools covered"}
              </span>
            </article>
            <article className="ct-metric-card">
              <small>Handwash Points</small>
              <strong>{fmt(handwashPoints)}</strong>
              <span className="ct-metric-sub">{avgHandwashPerSchool} avg / school</span>
            </article>
            <article className="ct-metric-card warning">
              <small>Sufficiency Ratio</small>
              <strong>{sufficiency.toFixed(2)}</strong>
              <span className="ct-metric-sub">Points per toilet</span>
            </article>
            <article className="ct-metric-card">
              <small>Essential Utilities</small>
              <strong>{pct(essentialUtilities, 0)}</strong>
              <span className="ct-metric-sub">Sanitary pad + electricity</span>
            </article>
          </div>
        </div>
      </section>

      <div className="ct-section-title">
        <div>
          <h2>Coverage Snapshot</h2>
          <p>
            Use fewer, more meaningful cards so officers can understand where hygiene availability is strong and
            where adequacy needs action.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-hw-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ct-score-grid">
        <ScoreCard
          icon={Droplets}
          iconTone="cyan"
          ringValue={handwashPct}
          ringDisplay={pct(handwashPct, 0)}
          title="Handwash Coverage"
          description={
            schoolsWithoutHandwash > 0
              ? `${fmt(schoolsWithoutHandwash)} schools still need handwash facility enablement.`
              : "All schools have reported handwash availability."
          }
          clickable={schoolsWithoutHandwash > 0}
          onClick={() => openCategory("no_handwash", schoolsWithoutHandwash)}
        />
        <ScoreCard
          icon={Droplets}
          iconTone="green"
          ringValue={waterPct}
          ringDisplay={pct(waterPct)}
          ringTone="blue"
          ringColor="#2563eb"
          title="Water Coverage"
          description={`Water availability is strong, but ${fmt(waterGap)} toilets need water enablement.`}
          clickable={waterGap > 0}
          onClick={() => openCategory("water_gap", waterGap)}
        />
        <ScoreCard
          icon={Heart}
          iconTone="purple"
          ringValue={sanitaryPct}
          ringDisplay={pct(sanitaryPct, 0)}
          ringTone="purple"
          ringColor="#8b5cf6"
          title="Sanitary Pad Access"
          description={
            schoolsWithoutSanitary > 0
              ? `${fmt(schoolsWithoutSanitary)} schools still need sanitary pad provision.`
              : "Coverage is universal across the current district dataset."
          }
          clickable={schoolsWithoutSanitary > 0}
          onClick={() => openCategory("no_sanitary_pad", schoolsWithoutSanitary)}
        />
        <ScoreCard
          icon={Zap}
          iconTone="orange"
          ringValue={electricityPct}
          ringDisplay={pct(electricityPct, 0)}
          title="Electricity Readiness"
          description={
            schoolsWithoutElectricity > 0
              ? `${fmt(schoolsWithoutElectricity)} schools still need electricity for hygiene operations.`
              : "Electricity availability is not a constraint for hygiene facilities."
          }
          clickable={schoolsWithoutElectricity > 0}
          onClick={() => openCategory("no_electricity", schoolsWithoutElectricity)}
        />
        <ScoreCard
          icon={BarChart3}
          iconTone=""
          ringValue={sufficiency * 100}
          ringDisplay={sufficiency.toFixed(2)}
          ringTone="blue"
          ringColor="#2563eb"
          ringMax={100}
          title="Point Sufficiency"
          description={
            pointSufficiencySchools > 0
              ? `${fmt(pointSufficiencySchools)} high-footfall schools need more handwash points per toilet.`
              : "Capacity ratio needs improvement in high-footfall schools."
          }
          clickable={pointSufficiencySchools > 0}
          onClick={() => openCategory("point_sufficiency", pointSufficiencySchools)}
        />
      </section>

      <div className="ct-section-title">
        <div>
          <h2>Performance Diagnostics</h2>
          <p>
            Charts are separated into diagnostic panels to avoid the compressed look and make the decision flow
            clearer.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-hw-blocks")}>
          Go to block ranking →
        </button>
      </div>

      <section className="ct-analytics-grid">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>WASH Compliance Radar</h3>
              <p>Balanced view across the main hygiene readiness dimensions.</p>
            </div>
            <span className="ct-pill">District average</span>
          </div>
          <WashRadarChart metrics={radarMetrics} />
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Facility Availability Distribution</h3>
              <p>Availability is high; focus should shift from presence to adequacy and maintenance frequency.</p>
            </div>
            <span className="ct-pill">Available vs gap</span>
          </div>
          <div className="ct-bar-list" aria-label="Facility availability bar chart">
            <FacilityBar labelLines={["Handwash", "Facility"]} value={handwashPct} tone="cyan" />
            <FacilityBar labelLines={["Sanitary", "Pad"]} value={sanitaryPct} tone="purple" />
            <FacilityBar labelLines={["Electricity"]} value={electricityPct} />
            <FacilityBar labelLines={["Toilet with", "Water"]} value={waterPct} tone="blue" />
            <FacilityBar labelLines={["Functional", "Toilets"]} value={functionalPct} />
          </div>
          <div className="ct-facility-list">
            <div className="ct-facility-item">
              <div>
                <b>High confidence</b>
                <span>
                  {handwashPct >= 95 && electricityPct >= 95 && sanitaryPct >= 95
                    ? "Handwash, electricity and sanitary pad availability are strong."
                    : "Handwash, sanitary pad and electricity coverage still need targeted block action."}
                </span>
              </div>
              <span className="ct-badge green">Stable</span>
            </div>
            <div className="ct-facility-item">
              <div>
                <b>Coverage gap</b>
                <span>{fmt(waterGap)} functional toilets still need water linkage.</span>
              </div>
              <span className="ct-badge orange">Action needed</span>
            </div>
          </div>
        </article>
      </section>

      <div className="ct-section-title" id="ct-hw-blocks">
        <div>
          <h2>Block Hygiene Readiness</h2>
          <p>
            Block-wise view highlights sufficiency and water gaps instead of showing repeated 100% bars across
            every block.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-hw-overview")}>
          Back to top ↑
        </button>
      </div>

      <section className="ct-two-column" id="ct-hw-actions">
        <article className="ct-panel ct-table-card">
          <div className="ct-table-head">
            <div>
              <h3>Block-Wise Hygiene Performance</h3>
              <p>
                Ranking based on WASH score, handwash coverage, sanitary pad coverage and water readiness.
              </p>
            </div>
            <span className="ct-pill">Top blocks</span>
          </div>
          <table className="ct-infra-table" aria-label="Block-wise hygiene performance">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Block</th>
                <th>WASH Score</th>
                <th>Handwash</th>
                <th>Sanitary Pad</th>
                <th>Electricity</th>
                <th>Water Readiness</th>
                <th>Sufficiency</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rankedBlocks.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: "center", color: "var(--ct-muted)", fontWeight: 600 }}>
                    No block hygiene data available for the selected scope.
                  </td>
                </tr>
              ) : (
                rankedBlocks.slice(0, 20).map((block) => {
                  const status = getBlockStatus(block.wash_index ?? 0, block.sufficiency_ratio ?? 0);
                  return (
                    <tr key={block.block_name}>
                      <td>{block.rank}</td>
                      <td>{block.block_name}</td>
                      <td className="ct-score">{block.wash_index?.toFixed(1)}</td>
                      <td>{pct(block.handwash_pct ?? handwashPct, 0)}</td>
                      <td>{pct(block.sanitary_pct ?? sanitaryPct, 0)}</td>
                      <td>{pct(block.electricity_pct ?? electricityPct, 0)}</td>
                      <td className="ct-progress-cell">
                        <div className="ct-mini-track">
                          <i style={{ width: `${Math.min(block.water_pct ?? 0, 100)}%` }} />
                        </div>
                      </td>
                      <td>{(block.sufficiency_ratio ?? sufficiency).toFixed(2)}</td>
                      <td>
                        <span className={`ct-badge ${status.tone}`}>{status.label}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Recommended Action Queue</h3>
              <p>Focused actions that turn WASH monitoring into officer follow-up.</p>
            </div>
            <span className="ct-pill">Priority</span>
          </div>
          <div className="ct-action-card">
            <div className="ct-action-item">
              <div className="ct-icon-box cyan">
                <Droplets size={22} />
              </div>
              <div>
                <h4>Improve handwash-point sufficiency</h4>
                <p>
                  Identify high-enrolment schools where points per toilet are below the district adequacy
                  threshold.
                </p>
                <div className="ct-action-meta">
                  <span className="ct-badge orange">30-day sprint</span>
                  <span className="ct-badge blue">Block officers</span>
                </div>
              </div>
            </div>
            <div className="ct-action-item">
              <div className="ct-icon-box green">
                <Bath size={22} />
              </div>
              <div>
                <h4>Close toilet water gaps</h4>
                <p>Prioritize the {fmt(waterGap)} functional toilets that are still not water-enabled.</p>
                <div className="ct-action-meta">
                  <span className="ct-badge red">High impact</span>
                  <span className="ct-badge blue">Engineering review</span>
                </div>
              </div>
            </div>
            <div className="ct-action-item">
              <div className="ct-icon-box purple">
                <Heart size={22} />
              </div>
              <div>
                <h4>Maintain sanitary pad supply assurance</h4>
                <p>
                  Keep monthly stock validation so 100% availability does not become only a one-time status.
                </p>
                <div className="ct-action-meta">
                  <span className="ct-badge green">Preventive</span>
                  <span className="ct-badge blue">Monthly audit</span>
                </div>
              </div>
            </div>
            <div className="ct-action-footer">
              <b>Next review recommendation</b>
              <span>
                Move from &ldquo;facility available&rdquo; reporting to &ldquo;facility usable and sufficient&rdquo;
                monitoring for every school and block.
              </span>
            </div>
          </div>
        </article>
      </section>

      <HygieneWashSchoolsModal
        open={Boolean(activeCategory)}
        category={activeCategory}
        onClose={() => setActiveCategory(null)}
        scopeLabel={scopeLabel}
      />
    </div>
  );
};

export default ClassroomsToiletsHygieneWashTab;
